import { useState } from 'react';
import type { DataExport } from '@pointfive/contracts';
import { api } from '@/client/api';
import { useProcedure } from '@/client/useProcedure';
import { ActionButton, Section, SettingCard, SettingRow, ToneChip } from './kit';

/** Settings → Data Management.
 *
 *  Export and erasure, and the honest difference between them: one is reversible and one is not.
 *
 *  The export is built from **the same table lists the purge deletes from** (§16.5). Two lists would drift, and the
 *  drift would be invisible in the worst direction — a table exported but never erased, or erased but never
 *  exported. The per-list row counts are shown so the trader can see it is complete rather than take it on trust.
 *
 *  Erasure asks for the handle typed out. Not friction for its own sake: it is the one action in the product with no
 *  undo after its hold expires, and a confirmation you can click through without reading is not a confirmation. The
 *  ledger is anonymised rather than deleted (DEC-043) and this screen says so, because a trader who believes their
 *  trade history is being destroyed and later finds it in an investor's report would be right to be angry.
 */
export function DataSection() {
  const { state, reload } = useProcedure(() => api.me.profile.get(), []);
  const { state: retentionState } = useProcedure(() => api.me.data.retention(), []);
  const retention = retentionState.status === 'ready' ? retentionState.data : null;
  const [exported, setExported] = useState<DataExport | null>(null);
  const [busy, setBusy] = useState<'export' | 'erase' | null>(null);
  const [handle, setHandle] = useState('');
  const [erasure, setErasure] = useState<{ requestId: string; scheduledFor: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runExport = async (): Promise<void> => {
    setBusy('export');
    setError(null);
    try {
      setExported(await api.me.data.export({ idempotencyKey: `export-${Date.now().toString(36)}` }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The export could not be produced.');
    } finally {
      setBusy(null);
    }
  };

  const requestErasure = async (): Promise<void> => {
    setBusy('erase');
    setError(null);
    try {
      const res = await api.me.erasure.request({
        idempotencyKey: `erasure-${Date.now().toString(36)}`,
        confirmHandle: handle,
      });
      setErasure({ requestId: res.requestId, scheduledFor: res.scheduledFor });
      setHandle('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The request could not be made.');
    } finally {
      setBusy(null);
    }
  };

  const cancelErasure = async (): Promise<void> => {
    if (!erasure) return;
    setBusy('erase');
    try {
      await api.me.erasure.cancel({ requestId: erasure.requestId });
      setErasure(null);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Section state={state} reload={reload}>
      {(profile) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <SettingCard
            title="Export"
            sub="Everything this account owns, in one JSON file, built from the same lists an erasure would delete from."
            extra={
              <ActionButton
                onClick={() => void runExport()}
                title="Produce a data export"
                disabled={busy !== null}
              >
                {busy === 'export' ? 'Preparing…' : 'Prepare export'}
              </ActionButton>
            }
          >
            {exported === null ? (
              <SettingRow
                label="No export prepared"
                hint="Preparing one gathers your profile, trades, sessions, journal, scores and settings."
              />
            ) : (
              <>
                <SettingRow
                  label="Export ready"
                  hint={`${(exported.bytes / 1024).toFixed(1)} KB · the link expires in fifteen minutes, because a permanent URL to your whole account outlives the reason you made it.`}
                >
                  {exported.url === null ? (
                    // Produced but not linkable — the object store was unavailable. Said plainly rather than shown
                    // as a link that would fail when clicked.
                    <ToneChip tone="warn">No link available</ToneChip>
                  ) : (
                    <a
                      href={exported.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '5px 12px',
                        borderRadius: '99px',
                        border: '1px solid var(--l8)',
                        color: 'var(--text-mid)',
                        textDecoration: 'none',
                      }}
                    >
                      Download
                    </a>
                  )}
                </SettingRow>
                {exported.contents.map((c) => (
                  <SettingRow
                    key={c.list}
                    label={c.list}
                    hint={`${c.rows.toLocaleString('en-US')} ${c.rows === 1 ? 'row' : 'rows'}`}
                  />
                ))}
              </>
            )}
          </SettingCard>

          <SettingCard
            title="Retention"
            sub="What is kept, and for how long. Nothing here is configurable — these are the commitments the system makes."
          >
            {/* Rendered from the policy the server serves, not restated here. These three sentences used to be
                hardcoded in this file while the machinery that actually destroys things lived in the erasure
                module — two descriptions of one policy, and the screen would have gone on saying whatever it said
                last long after the behaviour changed. */}
            {(retention?.classes ?? []).map((c) => (
              <SettingRow key={c.key} label={c.label} hint={c.reason} />
            ))}
            {retention === null ? <SettingRow label="Loading the retention policy…" /> : null}
          </SettingCard>

          <SettingCard
            title="Delete this account"
            sub="A thirty-day hold, then a purge. The hold exists because people change their minds; after it, there is no undo."
          >
            <SettingRow
              label="What is destroyed"
              hint="Your profile, journal entries and their audio, notifications, watchlists and settings."
              danger
            />
            <SettingRow
              label="What is anonymised instead"
              hint="Trades and score history are kept, owned by a tombstone that cannot resolve to a person. Destroying them would take an investor's evidence with them; leaving them named would ignore your request."
            />

            {erasure === null ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  paddingTop: '13px',
                  borderTop: '1px solid var(--l5)',
                  flexWrap: 'wrap',
                }}
              >
                <input
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  placeholder={`Type ${profile.handle} to confirm`}
                  aria-label={`Type your handle, ${profile.handle}, to confirm erasure`}
                  style={{
                    flex: 1,
                    minWidth: '200px',
                    fontSize: '11.5px',
                    fontFamily: 'inherit',
                    padding: '7px 11px',
                    borderRadius: '99px',
                    border: '1px solid var(--l8)',
                    background: 'var(--l3)',
                    color: 'var(--text)',
                  }}
                />
                <ActionButton
                  onClick={() => void requestErasure()}
                  title="Request erasure of this account"
                  disabled={busy !== null || handle !== profile.handle}
                  danger
                >
                  {busy === 'erase' ? 'Requesting…' : 'Request erasure'}
                </ActionButton>
              </div>
            ) : (
              <SettingRow
                label="Erasure requested"
                hint={`Scheduled for ${erasure.scheduledFor}. Withdraw it any time before then — that is what the hold is for.`}
                danger
              >
                <ActionButton
                  onClick={() => void cancelErasure()}
                  title="Withdraw the erasure request"
                  disabled={busy !== null}
                >
                  Withdraw
                </ActionButton>
              </SettingRow>
            )}

            {error === null ? null : (
              <div role="alert" style={{ fontSize: '11px', color: 'var(--neg)', paddingTop: '9px' }}>
                {error}
              </div>
            )}
          </SettingCard>
        </div>
      )}
    </Section>
  );
}
